<template>
  <el-card class="form-card" data-cy="tenant-bootstrap-card">
    <div class="card-header">
      <h3 id="tenant-bootstrap-title">Scheduling &amp; Rostering Bootstrap</h3>
    </div>
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p
        class="flex items-center text-sm"
        :class="view.tone"
        role="status"
        aria-live="polite"
        data-cy="tenant-bootstrap-status"
      >
        <i :class="view.icon" class="mr-2 text-lg" aria-hidden="true" />
        <span>{{ loadError || (isLoading ? 'Loading bootstrap status...' : view.message) }}</span>
      </p>
      <el-button
        v-if="loadError"
        size="small"
        data-cy="tenant-bootstrap-refresh-btn"
        @click="loadStatus"
      >
        Refresh
      </el-button>
      <el-button
        v-else
        type="primary"
        size="small"
        data-cy="tenant-bootstrap-btn"
        aria-describedby="tenant-bootstrap-title"
        :loading="isStarting || view.state === 'in-progress'"
        :disabled="isLoading || isStarting || view.buttonDisabled"
        @click="handleClick"
      >
        {{ view.buttonLabel }}
      </el-button>
    </div>
  </el-card>
</template>

<script>
import { API } from 'aws-amplify'
import {
  cardView,
  alreadyBootstrappedMessage,
  BOOTSTRAP_AGAIN_CONFIRM,
  POLL_INTERVAL_MS,
  FORBIDDEN_MESSAGE,
  isForbiddenError,
  loadErrorMessage,
} from '../tenantBootstrap'

export default {
  name: 'TenantBootstrapCard',
  props: {
    group: { type: String, required: true },
  },
  data() {
    return {
      status: null,
      isLoading: true,
      isStarting: false,
      loadError: '',
      pollTimer: null,
    }
  },
  computed: {
    view() {
      return cardView(this.status, this.getTenantTimeZone())
    },
  },
  mounted() {
    this.loadStatus()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async callBootstrap(mutation, again = false) {
      const post = { body: { type: 'tenant-bootstrap', mutation, group: this.group, again } }
      return safeFunction(API.post)('heraPublicApi', '/executeMutation', post)
    },

    async loadStatus() {
      this.isLoading = !this.status
      try {
        const result = await this.callBootstrap('get-status')
        this.loadError = ''
        this.applyStatus(result.status)
      } catch (e) {
        console.error('[tenant-bootstrap] get-status failed', e)
        this.loadError = loadErrorMessage(e)
        this.stopPolling()
      } finally {
        this.isLoading = false
      }
    },

    applyStatus(next) {
      const previous = this.status?.state
      this.status = next || null
      const state = this.view.state
      if (previous === 'in-progress' && state === 'done') {
        this.displayUserNotification({ title: 'Success', type: 'success', message: 'Scheduling & Rostering bootstrap finished.' })
      } else if (previous === 'in-progress' && state === 'failed') {
        this.displayUserNotification({ title: 'Error', type: 'error', message: 'Bootstrapping did not finish. Use Try Again to finish the missing parts.' })
      }
      if (state === 'in-progress') this.schedulePoll()
      else this.stopPolling()
    },

    schedulePoll() {
      this.stopPolling()
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null
        this.loadStatus()
      }, POLL_INTERVAL_MS)
    },

    stopPolling() {
      if (this.pollTimer) clearTimeout(this.pollTimer)
      this.pollTimer = null
    },

    async handleClick() {
      const again = this.view.isAgain
      if (again) {
        try {
          await this.$confirm(BOOTSTRAP_AGAIN_CONFIRM, 'Bootstrap Again', {
            confirmButtonText: 'Bootstrap Again',
            cancelButtonText: 'Cancel',
            type: 'info',
          })
        } catch (e) {
          return // cancelled
        }
      }
      await this.start(again)
    },

    async start(again) {
      this.isStarting = true
      try {
        const result = await this.callBootstrap('start', again)
        if (result.code === 'started') {
          this.displayUserNotification({ title: 'Bootstrapping', type: 'info', message: 'Bootstrapping has started. This can take a minute.' })
        } else if (result.code === 'already-bootstrapped') {
          // Built here so the toast uses the same date format and time zone as the card.
          this.displayUserNotification({ title: 'Info', type: 'info', message: alreadyBootstrappedMessage(result.status, this.getTenantTimeZone()) })
        } else if (result.code === 'in-progress') {
          this.displayUserNotification({ title: 'Info', type: 'info', message: result.message })
        }
        if (result.status) this.applyStatus(result.status)
      } catch (e) {
        if (isForbiddenError(e)) {
          this.loadError = FORBIDDEN_MESSAGE
          this.stopPolling()
        } else {
          const message = e?.response?.data?.message
          this.displayUserError(message || e)
        }
      } finally {
        this.isStarting = false
      }
    },
  },
}
</script>
